<script lang="ts">
  import { untrack } from "svelte";
  import { api, type AdminPayment } from "@/api";
  import { token, user } from "@/stores/session";
  import { routeHref } from "@/router";
  import { formatDateTime, formatNumber } from "@/utils/format";
  import AdminShell from "@/components/AdminShell.svelte";
  import Card from "@/components/Card.svelte";
  import Button from "@/components/Button.svelte";
  import Select from "@/components/Select.svelte";
  import TextInput from "@/components/TextInput.svelte";
  import Alert from "@/components/Alert.svelte";
  import EmptyState from "@/components/EmptyState.svelte";
  import LoadingState from "@/components/LoadingState.svelte";

  let query = $state("");
  let debouncedQuery = $state("");
  let status = $state("");
  let payments = $state<AdminPayment[]>([]);
  let page = $state(1);
  let cursors = $state<Array<string | null>>([null]);
  let nextCursor = $state<string | null>(null);
  let loading = $state(true);
  let busyId = $state<string | null>(null);
  let error = $state("");
  let actionError = $state("");
  let message = $state("");
  let listController: AbortController | null = null;
  let actionController: AbortController | null = null;
  let generation = 0;
  let searchTimer: ReturnType<typeof setTimeout> | null = null;

  function label(payment: AdminPayment): string {
    if (payment.creditedAt) {
      return "Кредиты зачислены";
    }
    const labels: Record<string, string> = {
      creating: "Создание счёта",
      pending: "Ожидает оплаты",
      process: "Обрабатывается",
      check: "Проверяется",
      confirm_check: "Ожидает подтверждения",
      paid: "Ожидает зачисления",
      paid_over: "Ожидает зачисления",
      wrong_amount: "Недоплата или неверная сумма",
      cancel: "Срок оплаты истёк",
      fail: "Платёж не завершён",
      system_fail: "Ошибка платежа",
      locked: "Платёж на проверке",
      refund_process: "Возврат обрабатывается",
      refund_fail: "Ошибка возврата",
      refund_paid: "Возвращён",
    };
    return labels[payment.status] ?? "Неизвестный статус";
  }

  async function load(cursor: string | null = null, targetPage = 1): Promise<void> {
    const currentToken = $token;
    if (!currentToken) {
      return;
    }
    listController?.abort();
    const controller = new AbortController();
    listController = controller;
    const currentGeneration = generation;
    loading = true;
    error = "";
    try {
      const result = await api.adminPayments(
        currentToken,
        debouncedQuery,
        status,
        controller.signal,
        cursor,
      );
      if (controller.signal.aborted || currentGeneration !== generation) {
        return;
      }
      payments = result.items;
      nextCursor = result.nextCursor;
      cursors = targetPage === 1 ? [null] : [...cursors.slice(0, targetPage - 1), cursor];
      page = targetPage;
    } catch (cause) {
      if (!controller.signal.aborted && currentGeneration === generation) {
        error = cause instanceof Error ? cause.message : "Не удалось загрузить платежи";
      }
    } finally {
      if (!controller.signal.aborted && currentGeneration === generation) {
        loading = false;
      }
    }
  }

  function search(event: SubmitEvent): void {
    event.preventDefault();
    if (searchTimer) {
      clearTimeout(searchTimer);
    }
    if (debouncedQuery !== query) {
      debouncedQuery = query;
    } else {
      void load();
    }
  }

  async function check(payment: AdminPayment): Promise<void> {
    const currentToken = $token;
    if (!currentToken || busyId) {
      return;
    }
    const controller = new AbortController();
    actionController = controller;
    const currentGeneration = generation;
    busyId = payment.id;
    actionError = "";
    message = "";
    try {
      const updated = await api.adminCheckPayment(currentToken, payment.id, controller.signal);
      if (controller.signal.aborted || currentGeneration !== generation) {
        return;
      }
      message = `Проверка завершена: ${label(updated).toLowerCase()}.`;
      await load();
    } catch (cause) {
      if (!controller.signal.aborted && currentGeneration === generation) {
        actionError = cause instanceof Error ? cause.message : "Не удалось проверить платёж";
      }
    } finally {
      if (!controller.signal.aborted && currentGeneration === generation) {
        busyId = null;
      }
    }
  }

  $effect(() => {
    const term = query;
    searchTimer = setTimeout(() => {
      debouncedQuery = term;
    }, 300);
    return () => {
      if (searchTimer) {
        clearTimeout(searchTimer);
      }
    };
  });

  $effect(() => {
    const currentToken = $token;
    const userId = $user?.id;
    void debouncedQuery;
    void status;
    generation++;
    payments = [];
    page = 1;
    cursors = [null];
    nextCursor = null;
    busyId = null;
    actionError = "";
    message = "";
    if (currentToken && userId) {
      untrack(() => void load());
    } else {
      loading = false;
    }
    return () => {
      listController?.abort();
      actionController?.abort();
    };
  });
</script>

<AdminShell section="payments">
  <Card class="admin-card admin-wide-card">
    <div class="admin-toolbar">
      <div>
        <p class="eyebrow">Пополнение кредитов</p>
        <h2>Платежи пользователей</h2>
      </div>
      <form class="admin-search payment-search" onsubmit={search}>
        <TextInput
          density="compact"
          flexible
          bind:value={query}
          maxlength={200}
          placeholder="Счёт, пользователь или почта"
          aria-label="Поиск платежей"
        />
        <Select
          bind:value={status}
          aria-label="Статус платежа"
          options={[
            { value: "", label: "Все платежи" },
            { value: "credited", label: "Зачислены" },
            { value: "pending", label: "Ожидают оплаты" },
            { value: "failed", label: "Не завершены" },
          ]}
        />
        <Button type="submit" disabled={loading}>Найти</Button>
      </form>
    </div>
    <p class="muted">
      Найдите счёт по его номеру, номеру Heleket, аккаунту, имени или почте пользователя. Проверка
      запрашивает актуальный статус у Heleket.
    </p>
    {#if error}<Alert type="error"
        >{error}<Button
          variant="ghost"
          size="compact"
          disabled={loading}
          onclick={() => void load(cursors[page - 1], page)}>Повторить загрузку</Button
        ></Alert
      >{/if}
    {#if actionError}<Alert type="error">{actionError}</Alert>{/if}
    {#if message}<p role="status">{message}</p>{/if}
    {#if loading}
      <LoadingState message="Загрузка платежей…" />
    {:else if !error && payments.length === 0}
      <EmptyState title="Платежи не найдены" message="Попробуйте изменить поиск или статус." />
    {:else if !error}
      <ul class="payment-list">
        {#each payments as payment (payment.id)}
          <li class="payment">
            <div class="payment-summary">
              <div>
                <strong
                  >{formatNumber(payment.amountRubles)} ₽ → {formatNumber(payment.credits)} кредитов</strong
                >
                <span class:credited={!!payment.creditedAt}>{label(payment)}</span>
                <small>{formatDateTime(payment.createdAt)}</small>
              </div>
              <div class="payment-owner">
                <a href={routeHref("admin-user", { id: payment.userId })}>{payment.username}</a
                ><small>{payment.email}</small>
              </div>
              <Button
                variant="ghost"
                size="compact"
                disabled={busyId !== null}
                onclick={() => void check(payment)}
                >{busyId === payment.id ? "Проверяем…" : "Проверить платёж"}</Button
              >
            </div>
            <details>
              <summary>Данные счёта</summary>
              <dl>
                <div>
                  <dt>Номер счёта</dt>
                  <dd>{payment.id}</dd>
                </div>
                <div>
                  <dt>Номер Heleket</dt>
                  <dd>{payment.providerId ?? "Ещё не получен"}</dd>
                </div>
                <div>
                  <dt>Пользователь</dt>
                  <dd>{payment.userId}</dd>
                </div>
                <div>
                  <dt>Бонус</dt>
                  <dd>{payment.bonusPercent}%</dd>
                </div>
                <div>
                  <dt>Статус Heleket</dt>
                  <dd>{payment.status}</dd>
                </div>
                <div>
                  <dt>Последняя проверка</dt>
                  <dd>
                    {payment.checkedAt ? formatDateTime(payment.checkedAt) : "Ещё не проверялся"}
                  </dd>
                </div>
                <div>
                  <dt>Зачисление</dt>
                  <dd>{payment.creditedAt ? formatDateTime(payment.creditedAt) : "Не зачислен"}</dd>
                </div>
              </dl>
            </details>
          </li>
        {/each}
      </ul>
    {/if}
    <div class="pagination">
      <span>Страница {page}</span>
      <Button
        variant="ghost"
        size="compact"
        disabled={page === 1 || loading || busyId !== null}
        onclick={() => void load(cursors[page - 2], page - 1)}>Назад</Button
      >
      <Button
        variant="ghost"
        size="compact"
        disabled={!nextCursor || loading || busyId !== null}
        onclick={() => void load(nextCursor, page + 1)}>Далее</Button
      >
    </div>
  </Card>
</AdminShell>

<style>
  .payment-search {
    flex-wrap: wrap;
  }
  .payment-list {
    margin: 1.5rem 0;
    padding: 0;
    list-style: none;
  }
  .payment {
    border-top: 1px solid var(--line);
    padding: 1.2rem 0;
  }
  .payment-summary {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
    gap: 1rem;
    align-items: center;
  }
  .payment-summary div {
    display: grid;
    gap: 0.35rem;
    min-width: 0;
  }
  .payment-summary small,
  .payment-summary span,
  dt {
    color: var(--muted);
  }
  .payment-summary .credited {
    color: var(--green);
  }
  .payment-owner small,
  dd {
    overflow-wrap: anywhere;
  }
  details {
    margin-top: 1rem;
  }
  summary {
    cursor: pointer;
    color: var(--muted);
  }
  dl {
    margin: 1rem 0 0;
    display: grid;
    gap: 0.65rem;
  }
  dl div {
    display: grid;
    grid-template-columns: 11rem minmax(0, 1fr);
    gap: 0.75rem;
  }
  dd {
    margin: 0;
    user-select: text;
  }
  @media (max-width: 700px) {
    .payment-summary {
      grid-template-columns: minmax(0, 1fr);
    }
    dl div {
      grid-template-columns: minmax(0, 1fr);
      gap: 0.2rem;
    }
  }
</style>
